import { useState } from "react";

import { ApiFehler } from "../../api/client";
import { usePortalKundenLink, usePortalKundenLinkErzeugen } from "../../api/hooks";
import type { VollmachtModell } from "../../api/typen";
import { useToast } from "../../kontext/ToastKontext";
import { datum, zeitpunkt } from "../../lib/format";
import { kopiereText, linkFuerDieseAdresse } from "../../lib/zwischenablage";
import { Hinweis, Karte, Knopf, Spinner } from "../Basis";

// Die Vollmacht geht immer an den Betrieb (mit Untervollmacht für Dienstleister). Ihr Kunde sieht nur Ihren Betrieb.
const VARIANTEN: { wert: VollmachtModell | null; titel: string; text: string }[] = [
  { wert: "betrieb", titel: "Mit Vollmacht", text: "Ihr Kunde bevollmächtigt Ihren Betrieb mit der Anmeldung." },
  { wert: null, titel: "Ohne Vollmacht", text: "Die Vollmacht liegt schon vor – nur Angaben abfragen." },
];

/**
 * Portal: Kunden-Link. Der Kunde gibt Daten, Zähler und Bankverbindung selbst ein und unterschreibt die
 * Vollmacht – per Link aus der Ferne oder direkt auf dem Gerät des Installateurs vor Ort.
 */
export function KundenLinkKarte({ anlageId }: { anlageId: number }) {
  const stand = usePortalKundenLink(anlageId);
  const erzeugen = usePortalKundenLinkErzeugen(anlageId);
  const toast = useToast();
  const [modell, setModell] = useState<VollmachtModell | null>("betrieb");
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (stand.isLoading) {
    return (
      <Karte className="p-5">
        <Spinner label="Wird geladen…" />
      </Karte>
    );
  }
  const link = stand.data?.link ?? null;
  const offen = link?.status === "offen" && link.link;
  const url = offen ? linkFuerDieseAdresse(link.link as string) : null;

  function linkErzeugen() {
    setFehler(null);
    erzeugen.mutate(modell, {
      onSuccess: () => {
        setNeu(false);
        toast.erfolg("Link erzeugt");
      },
      onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Link konnte nicht erzeugt werden"),
    });
  }

  async function teilen() {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Ihre Angaben zur Anlagen-Anmeldung", text: "Bitte hier Ihre Angaben machen und die Vollmacht unterschreiben:", url });
        return;
      } catch {
        // abgebrochen oder nicht möglich: kopieren
      }
    }
    if (await kopiereText(url)) toast.erfolg("Link kopiert", "Jetzt z. B. per WhatsApp oder SMS an den Kunden senden.");
    else toast.fehler(new Error("Kopieren nicht möglich"));
  }

  const auswahl = (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-semibold">Vollmacht</legend>
      {VARIANTEN.map((v) => (
        <label key={String(v.wert)} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${modell === v.wert ? "border-neutral-900" : "border-neutral-200"}`}>
          <input type="radio" name={`vollmacht-${anlageId}`} className="mt-1 h-4 w-4 accent-neutral-900" checked={modell === v.wert} onChange={() => setModell(v.wert)} />
          <span>
            <span className="block text-sm font-semibold">{v.titel}</span>
            <span className="block text-xs text-neutral-500">{v.text}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );

  return (
    <Karte className="space-y-4 p-5">
      <div>
        <h2 className="font-semibold">Kunde füllt selbst aus</h2>
        <p className="mt-1 text-sm text-neutral-600">
          Ihr Kunde gibt seine Daten, Zählernummer und Bankverbindung selbst ein und unterschreibt die Vollmacht mit dem Finger –
          per Link aus der Ferne oder direkt hier auf Ihrem Gerät.
        </p>
      </div>

      {link?.status === "abgeschlossen" && !neu && (
        <>
          <Hinweis art="erfolg">
            Ihr Kunde hat seine Angaben am {zeitpunkt(link.abgeschlossen_am as string)} abgeschickt
            {link.vollmacht_modell ? " und die Vollmacht unterschrieben" : ""}. ✓
          </Hinweis>
          <Knopf variante="geist" className="w-full" onClick={() => setNeu(true)}>Neuen Link erzeugen</Knopf>
        </>
      )}

      {offen && !neu && (
        <>
          <div className="grid gap-2">
            <Knopf variante="primaer" className="w-full !py-3" onClick={() => void teilen()}>Link an Kunden senden</Knopf>
            <a href={url ?? "#"} target="_blank" rel="noreferrer" className="block w-full rounded-lg border border-neutral-300 px-4 py-3 text-center text-sm font-semibold hover:bg-neutral-50">
              Hier auf diesem Gerät ausfüllen lassen
            </a>
          </div>
          <p className="text-xs text-neutral-500">
            Gültig bis {datum(link.gueltig_bis)} · {link.vollmacht_modell ? "mit Vollmacht" : "ohne Vollmacht"}
          </p>
          <button type="button" className="text-xs font-semibold text-neutral-600 underline" onClick={() => setNeu(true)}>
            Andere Variante / neuen Link erzeugen
          </button>
        </>
      )}

      {(!link || neu || link.status === "abgelaufen" || link.status === "gesperrt") && (
        <>
          {link?.status === "abgelaufen" && !neu && <Hinweis art="info">Der letzte Link ist abgelaufen.</Hinweis>}
          {auswahl}
          {neu && offen && <p className="text-xs text-neutral-500">Der bisherige Link wird dabei ungültig.</p>}
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          <Knopf variante="primaer" className="w-full !py-3" laedt={erzeugen.isPending} onClick={linkErzeugen}>
            Link erzeugen
          </Knopf>
          {neu && <Knopf variante="geist" className="w-full" onClick={() => setNeu(false)}>Abbrechen</Knopf>}
        </>
      )}
    </Karte>
  );
}
